import React, { useState, useEffect, lazy, Suspense } from 'react';
import { formatDateTime } from '../../utils/dateUtils';
import styled from 'styled-components';
import { optionsApi } from '../../services/api';
import { toast } from 'react-toastify';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FaTimes, FaQuestionCircle } from 'react-icons/fa';
import { useTheme } from '../../context/ThemeContext';

// Lazy load components
const TickerSearch = lazy(() => import('./TickerSearch'));
const OptionsParameters = lazy(() => import('./OptionsParameters'));
const OptionsChain = lazy(() => import('./OptionsChain'));
const Watchlist = lazy(() => import('./Watchlist'));

// Loading fallback
const ComponentLoader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  width: 100%;
  background-color: var(--card-bg-color);
  border-radius: 8px;
  margin-bottom: 1rem;
  color: var(--text-color);
  transition: background-color 0.3s ease, color 0.3s ease;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem;
  overflow-x: hidden;
  background-color: var(--bg-color);
  color: var(--text-color);
  transition: background-color 0.3s ease, color 0.3s ease;

  @media (max-width: 1200px) {
    padding: 0.75rem;
    max-width: 100%;
  }

  @media (max-width: 768px) {
    padding: 0.5rem;
    margin: 0;
    width: 100%;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;

  @media (max-width: 1200px) {
    gap: 0.6rem;
    margin-bottom: 1.25rem;
  }

  @media (max-width: 768px) {
    gap: 0.5rem;
    margin-bottom: 1rem;
    justify-content: space-between;
  }
`;

const Title = styled.h1`
  margin: 0; 
  color: var(--text-color);
  font-size: 1.75rem;
  transition: color 0.3s ease;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const Tooltip = styled.span`
  visibility: hidden;
  opacity: 0;
  width: 300px;
  background-color: ${props => props.theme.mode === 'dark' ? '#333' : '#333'};
  color: ${props => props.theme.mode === 'dark' ? '#f0f0f0' : '#fff'};
  text-align: center;
  border-radius: 6px;
  padding: 8px 12px;
  position: absolute;
  z-index: 1000;
  top: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  transition: opacity 0.3s, visibility 0.3s, background-color 0.3s ease, color 0.3s ease;
  font-size: 0.9rem;
  font-weight: normal;
  box-shadow: 0 2px 8px rgba(0,0,0,${props => props.theme.mode === 'dark' ? '0.3' : '0.15'});
  pointer-events: none;
  white-space: normal;
  word-wrap: break-word;

  @media (max-width: 768px) {
    width: 250px;
    font-size: 0.8rem;
    padding: 6px 10px;
    top: calc(100% + 8px);
  }

  &::before { 
    content: "";
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-width: 6px;
    border-style: solid;
    border-color: transparent transparent ${props => props.theme.mode === 'dark' ? '#333' : '#333'} transparent;
    transition: border-color 0.3s ease;
  }
`;
const InfoIconContainer = styled.div`
  position: relative;
  display: inline-flex; 
  align-items: center;
  cursor: help;
  padding: 4px;
  margin-left: 8px;

  &:hover ${Tooltip} {
    visibility: visible;
    opacity: 1;
  }

  @media (max-width: 768px) {
    padding: 2px;
    margin-left: 4px;
  }
`;

const InfoIcon = styled(FaQuestionCircle)`
  color: ${props => props.theme.mode === 'dark' ? '#90caf9' : '#2a5298'};
  font-size: 1.3rem;
  transition: color 0.3s ease;
  filter: ${props => props.theme.mode === 'dark' ? 'drop-shadow(0 0 2px rgba(144, 202, 249, 0.3))' : 'none'};
`;
const DialogOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const DialogContent = styled.div`
  background-color: ${props => props.theme.mode === 'dark' ? '#1a1a1a' : 'white'};
  border-radius: 8px;
  padding: 1.5rem;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, ${props => props.theme.mode === 'dark' ? '0.3' : '0.15'});
  margin: 1rem;
  color: ${props => props.theme.mode === 'dark' ? '#e0e0e0' : '#333'};
  transition: background-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease;

  @media (max-width: 1200px) {
    padding: 1.25rem;
    width: 450px;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    width: calc(100% - 2rem);
    margin: 1rem;
    max-height: calc(100vh - 2rem);
    overflow-y: auto;
  }
`;

const DialogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const DialogTitle = styled.h3`
  margin: 0;
  color: var(--text-color);
  font-size: 1.25rem;
  font-weight: 600;
  transition: color 0.3s ease;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.25rem;
  color: ${props => props.theme.mode === 'dark' ? '#aaa' : '#666'};
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;

  &:hover {
    color: ${props => props.theme.mode === 'dark' ? '#fff' : '#333'};
  }
`;

const DialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 1rem;
  margin-bottom: 1rem;
  cursor: pointer;
  font-size: 0.9rem;
  color: ${props => props.theme.mode === 'dark' ? '#d0d0d0' : '#555'};
  transition: color 0.3s ease;
  
  &:hover {
    color: ${props => props.theme.mode === 'dark' ? '#ffffff' : '#333'};
  }
`;

const CheckboxInput = styled.input`
  margin-right: 0.5rem;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
  white-space: nowrap;
  min-height: 38px;

  @media (max-width: 1200px) {
    padding: 0.45rem 0.9rem;
    font-size: 0.95rem;
    min-height: 36px;
  }

  @media (max-width: 768px) {
    padding: 0.4rem 0.8rem;
    font-size: 0.9rem;
    min-height: 34px;
  }
`;

const CancelButton = styled(Button)`
  background-color: ${props => props.theme.mode === 'dark' ? '#424242' : '#e0e0e0'};
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-size: 0.9rem;
  color: ${props => props.theme.mode === 'dark' ? '#e0e0e0' : '#333'};
  transition: background-color 0.3s ease, color 0.3s ease, transform 0.2s ease;

  &:hover {
    background-color: ${props => props.theme.mode === 'dark' ? '#616161' : '#d5d5d5'};
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const ConfirmButton = styled(Button)`
  background-color: ${props => props.theme.mode === 'dark' ? '#2196f3' : '#4a90e2'};
  border: 1px solid ${props => props.theme.mode === 'dark' ? '#1976d2' : '#3a80d2'};
  color: white;
  transition: background-color 0.3s ease, border-color 0.3s ease, transform 0.2s ease, box-shadow 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, ${props => props.theme.mode === 'dark' ? '0.3' : '0.1'});

  &:hover {
    background-color: ${props => props.theme.mode === 'dark' ? '#1976d2' : '#3a80d2'};
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, ${props => props.theme.mode === 'dark' ? '0.4' : '0.2'});
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, ${props => props.theme.mode === 'dark' ? '0.3' : '0.1'});
  }
`;

const OptionsWatchlist = () => {
  const { theme } = useTheme();
  const [ticker, setTicker] = useState('');
  const [expirations, setExpirations] = useState([]);
  const [selectedExpiration, setSelectedExpiration] = useState('');
  const [optionType, setOptionType] = useState('call');
  const [optionsChain, setOptionsChain] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdatingWatchlist, setIsUpdatingWatchlist] = useState(false);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [pendingContract, setPendingContract] = useState(null);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  const [disableDuplicateWarning, setDisableDuplicateWarning] = useState(false);
  const [tickerError, setTickerError] = useState('');
  const [loadingItems, setLoadingItems] = useState({});

  // Function to check if it's a trading day (Mon-Fri)
  const isTradingDay = (date) => {
    const day = date.getDay();
    return day >= 1 && day <= 5; // 1 is Monday, 5 is Friday
  };

  // Function to check if market is closed (after 4 PM ET)
  // No need for timezone conversion since Vercel server runs in Eastern Time
  const isMarketClosed = () => {
    const hours = new Date().getHours();
    return hours >= 16; // 4 PM or later
  };

  // Helper function to compare just the date portions of two Date objects
  const getTodayDate = () => {
    const now = new Date();
    // Return only the date portion in format YYYY-MM-DD
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };

  // Get a Date object for today at midnight (for date comparisons)
  const getTodayDateObject = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  // Compare if a date string matches today's date
  const isToday = (dateString) => {
    // Use string comparison for reliability
    return dateString === getTodayDate();
  };

  // Function to check if two dates are the same day
  const isSameDay = (date1, date2) => {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  };

  useEffect(() => {
    // Load watchlist from localStorage
    const storedWatchlist = localStorage.getItem('optionsWatchlist');
    if (storedWatchlist) {
      try {
        const parsedData = JSON.parse(storedWatchlist);
        if (Array.isArray(parsedData)) {
          setWatchlist(parsedData);
        } else if (parsedData.items && Array.isArray(parsedData.items)) {
          setWatchlist(parsedData.items);
        }
      } catch (error) {
        console.error('Error parsing watchlist:', error);
        setWatchlist([]);
        localStorage.setItem('optionsWatchlist', JSON.stringify({
          items: [],
          totalPNL: 0,
          lastUpdated: formatDateTime(new Date())
        }));
      }
    }

    // Set up interval to check if it's time to auto-refresh (check every minute)
    const intervalId = setInterval(() => {
      const now = new Date();
      const isTradeDay = isTradingDay(now);

      if (!isTradeDay) {
        return; // Not a trading day, skip check
      }

      const hours = now.getHours();
      const minutes = now.getMinutes();

      // Check if it's 4:00 PM ET on a trading day
      // No timezone conversion needed since Vercel runs in Eastern Time
      if (isTradeDay && hours === 16 && minutes === 0) {
        console.log('Auto-refreshing contracts at market close (4:00 PM ET)');
        toast.info('Auto-refreshing contracts at market close');
        updateWatchlistPrices();
      }
    }, 30000); // Check every 30 seconds to avoid missing the exact minute

    // Clean up interval when component unmounts
    return () => clearInterval(intervalId);
  }, []); // Empty dependency array to run only once on mount

  // Get location from React Router
  const location = useLocation();
  const navigate = useNavigate();
  
  // Effect to handle URL ticker parameters
  useEffect(() => {
    // Check for ticker parameter in URL query
    const searchParams = new URLSearchParams(location.search);
    const queryTickerParam = searchParams.get('ticker');

    const tickerParam = queryTickerParam;

    if (tickerParam) {
      const formattedTicker = tickerParam.toUpperCase();
      setTicker(formattedTicker);
      fetchExpirations(formattedTicker);
    }
  }, []);

  // Global function to save watchlist to localStorage
  const saveWatchlist = (updatedList) => {
    // Use the provided updated list or fall back to current watchlist
    const listToSave = updatedList || watchlist;

    // Calculate total PNL from the watchlist
    const totalPNL = listToSave.reduce((total, item) => total + (item.PNL_USD || 0), 0);

    console.log(`saveWatchlist: Saving ${listToSave.length} contracts. First contract:`, {
      symbol: listToSave[0]?.contractSymbol,
      currentPrice: listToSave[0]?.currentPrice,
      isApiExpired: listToSave[0]?.isApiExpired
    });

    // Update state with the watchlist
    setWatchlist(listToSave);

    // Save to localStorage
    localStorage.setItem('optionsWatchlist', JSON.stringify({
      items: listToSave,
      totalPNL: totalPNL,
      lastUpdated: formatDateTime(new Date())
    }));

    return listToSave;
  };

  // Effect to watch for changes to the watchlist
  // DISABLED: This useEffect was causing race conditions where it would save old state
  // before the new state was properly set, causing price flickering
  // The saveWatchlist function handles all localStorage updates now
  /*
  useEffect(() => {
    if (watchlist.length > 0) {
      console.log(`⚠️ useEffect triggered: Saving ${watchlist.length} contracts to localStorage. First contract:`, {
        symbol: watchlist[0]?.contractSymbol,
        currentPrice: watchlist[0]?.currentPrice,
        isApiExpired: watchlist[0]?.isApiExpired
      });
      // This only handles localStorage updates without re-sorting
      // Full sorting happens in the saveWatchlist function
      const totalPNL = watchlist.reduce((total, item) => total + (item.PNL_USD || 0), 0);
      localStorage.setItem('optionsWatchlist', JSON.stringify({
        items: watchlist,
        totalPNL: totalPNL,
        lastUpdated: formatDateTime(new Date())
      }));
    } else {
      localStorage.removeItem('optionsWatchlist');
    }
  }, [watchlist]);
  */

  useEffect(() => {
    if (ticker) {
      fetchExpirations();
    } else {
      setOptionsChain([]);
    }
  }, [ticker]);

  useEffect(() => {
    if (ticker && selectedExpiration && optionType) {
      fetchOptionsChain();
    }
  }, [ticker, selectedExpiration, optionType]);

  const fetchExpirations = async () => {
    if (!ticker) return;

    try {
      setIsLoading(true);
      setOptionsChain([]);
      setTickerError('');
      const response = await optionsApi.getExpirations(ticker);

      // Check if the response has the new format with warning and expirations fields
      if (response && typeof response === 'object' && 'expirations' in response) {
        // New response format
        const { expirations, warning } = response;

        // If there's a warning, show it to the user
        if (warning) {
          toast.warning(warning, {
            position: "top-right",
            autoClose: 7000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true
          });
        }

        if (expirations && expirations.length > 0) {
          setExpirations(expirations);
          const currentExpirationIsValid = selectedExpiration &&
            expirations.some(exp => exp.value === selectedExpiration);

          if (!selectedExpiration || !currentExpirationIsValid) {
            setSelectedExpiration(expirations[0].value);
            return expirations[0].value;
          }
        } else {
          setExpirations([]);
          setSelectedExpiration('');
          setOptionsChain([]);
        }
      } else if (response && Array.isArray(response) && response.length > 0) {
        // Old response format (array of expirations)
        setExpirations(response);
        const currentExpirationIsValid = selectedExpiration &&
          response.some(exp => exp.value === selectedExpiration);

        if (!selectedExpiration || !currentExpirationIsValid) {
          setSelectedExpiration(response[0].value);
          return response[0].value;
        }
      } else {
        setExpirations([]);
        setSelectedExpiration('');
        setOptionsChain([]);
        setTickerError('Ticker not found');
        toast.info("Ticker not found. Try another symbol.", {
          position: "top-right",
          autoClose: 5000
        });
      }
    } catch (error) {
      console.error('Error fetching expirations:', error);
      setExpirations([]);
      setSelectedExpiration('');
      setOptionsChain([]);
      setTickerError('Ticker not found');
      toast.error("Ticker not found. Please try another symbol.", {
        position: "top-right",
        autoClose: 5000
      });
    } finally {
      if (!selectedExpiration) {
        setIsLoading(false);
      }
    }
    return null;
  };

  const fetchOptionsChain = async (expiration = selectedExpiration) => {
    if (ticker && expiration) {
      setIsLoading(true);
      try {
        const chain = await optionsApi.getOptionsChain(
          ticker,
          expiration,
          optionType.toLowerCase()
        );
        setOptionsChain(chain);
      } catch (error) {
        console.error('Error fetching options chain:', error);
        toast.error('Failed to fetch options chain');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleTickerChange = (newTicker) => {
    if (newTicker === ticker) {
      return;
    }

    setTickerError('');
    setTicker(newTicker);
    setSelectedExpiration('');
    setOptionsChain([]);
    fetchExpirations(newTicker);

    // Update URL with the new ticker as a query parameter
    navigate(`/watchlist?ticker=${newTicker}`, { replace: true });
  };
  
  // Function to reset/clear the ticker symbol
  const resetTicker = () => {
    setTicker('');
    setTickerError('');
    setSelectedExpiration('');
    setOptionsChain([]);
    setExpirations([]);
    
    // Clear ticker from URL
    navigate('/watchlist', { replace: true });
    
    // Show toast notification
    toast.info('Ticker symbol has been reset');
  };

  const handleExpirationChange = (expiration) => {
    if (expiration !== selectedExpiration) {
      setSelectedExpiration(expiration);
    }
  };

  const handleOptionTypeChange = (type) => {
    setOptionType(type);
  };

  const isDuplicateContract = (contract) => {
    return watchlist.some(item => item.contractSymbol === contract.contractSymbol);
  };

  const handleDuplicateWarningResponse = (shouldAdd) => {
    if (shouldAdd && pendingContract) {
      addContractToWatchlist(pendingContract);
      toast.success('Contract added to watchlist');
    }

    if (dontShowAgain) {
      setDisableDuplicateWarning(true);
      localStorage.setItem('disableDuplicateWarning', 'true');
    }

    setShowDuplicateWarning(false);
    setPendingContract(null);
    setDontShowAgain(false);
  };

  const addToWatchlist = (contract) => {
    if (isDuplicateContract(contract) && !disableDuplicateWarning) {
      setPendingContract(contract);
      setShowDuplicateWarning(true);
      return;
    }

    addContractToWatchlist(contract);
  };

  const addContractToWatchlist = (contract) => {
    // Check if contract already exists in watchlist
    const existingContract = watchlist.find(item => item.contractSymbol === contract.contractSymbol);
    if (existingContract) {
      setPendingContract(contract);
      setShowDuplicateWarning(true);
      toast.warning('Contract already exists in watchlist', {
        position: "bottom-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
      });
      return;
    }
    const now = new Date();

    const midpointPrice = contract.bid > 0 && contract.ask > 0 ?
      Math.floor((contract.bid + contract.ask) * 100 / 2) / 100 :
      (contract.lastPrice || 0);

    const newItem = {
      underlyingPrice: parseFloat(contract.underlyingPrice || 0),
      ...contract,
      expiration: contract.expiration,
      selectedPrice: midpointPrice,
      currentPrice: midpointPrice,
      lastPrice: contract.lastPrice || 0,
      bid: contract.bid || 0,
      ask: contract.ask || 0,
      // Explicitly set flags for proper tab categorization
      isApiExpired: false,
      isExpiringToday: isExpiringToday(contract),
      addedAt: now.toISOString(),
      lastUpdated: now.toISOString()
    };

    if (newItem.currentPrice && newItem.selectedPrice > 0) {
      const pnlUsd = (newItem.currentPrice - newItem.selectedPrice) * 100;
      const pnlPct = ((newItem.currentPrice - newItem.selectedPrice) / newItem.selectedPrice) * 100;
      newItem.PNL_USD = pnlUsd;
      newItem.PNL_PCT = pnlPct;
    } else {
      newItem.PNL_USD = 0;
      newItem.PNL_PCT = 0;
    }

    const newWatchlist = [...watchlist, newItem];

    const totalPnL = newWatchlist.reduce((total, item) => total + (item.PNL_USD || 0), 0);

    setWatchlist(newWatchlist);
    localStorage.setItem('optionsWatchlist', JSON.stringify({
      items: newWatchlist,
      totalPnL: totalPnL,
      lastUpdated: formatDateTime(new Date())
    }));
  };

  const removeFromWatchlist = (index) => {
    const updatedWatchlist = [...watchlist];
    updatedWatchlist.splice(index, 1);

    const totalPnL = updatedWatchlist.reduce((total, item) => total + (item.PNL_USD || 0), 0);

    setWatchlist(updatedWatchlist);
    localStorage.setItem('optionsWatchlist', JSON.stringify({
      items: updatedWatchlist,
      totalPnL: totalPnL,
      lastUpdated: formatDateTime(new Date())
    }));
  };

  const clearWatchlist = (contractsToKeep = []) => {
    // Close the confirmation dialog first
    setShowClearConfirmation(false);

    if (contractsToKeep.length > 0) {
      // Only clear specific contracts, keeping others
      setWatchlist(contractsToKeep);

      // Update localStorage
      const totalPNL = contractsToKeep.reduce((total, item) => total + (item.PNL_USD || 0), 0);
      localStorage.setItem('optionsWatchlist', JSON.stringify({
        items: contractsToKeep,
        totalPNL,
        lastUpdated: formatDateTime(new Date())
      }));

      toast.success(`Selected contracts cleared, ${contractsToKeep.length} contracts kept`);
    } else {
      // Clear all contracts
      setWatchlist([]);
      localStorage.removeItem('optionsWatchlist');
      toast.success('All contracts cleared');
    }
  };

  const handleClearWatchlist = (contractsToKeep = []) => {
    if (watchlist.length === 0) {
      toast.info('Watchlist is already empty');
      return;
    }

    // If contractsToKeep is provided, clear only specific contracts
    if (contractsToKeep.length > 0) {
      clearWatchlist(contractsToKeep);
    } else {
      // Otherwise show the confirmation dialog
      setShowClearConfirmation(true);
    }
  };

  // Function to check if a contract is expiring today or on the next trading day (for the Expiring Today tab)
  const isExpiringToday = (contract) => {
    // First check for explicit flag added by test script
    if (contract.isExpiringToday === true) {
      console.log(`Contract ${contract.contractSymbol} has isExpiringToday flag - sending to Expiring Today tab`);
      return true;
    }

    if (!contract || !contract.expiration) return false;

    // If contract has been explicitly marked as expired by API, it's not eligible
    if (contract.isApiExpired === true) {
      return false;
    }

    try {
      // Get today's date and the contract expiration date
      const todayStr = getTodayDate();
      const expirationStr = contract.expiration;
      
      // Debug logging for date comparison
      console.log(`Comparing dates for ${contract.contractSymbol}: Today=${todayStr}, Expiration=${expirationStr}`);
      
      // Parse dates as objects for comparison
      const today = new Date();
      const expirationDate = new Date(expirationStr);
      
      // Set both to midnight for proper date comparison
      today.setHours(0, 0, 0, 0);
      expirationDate.setHours(0, 0, 0, 0);
      
      // First simple check: if the expiration date string exactly matches today's date string
      if (expirationStr === todayStr) {
        console.log(`Contract ${contract.contractSymbol} expires TODAY (${expirationStr}) - sending to Expiring Today tab`);
        return true;
      }
      
      // Next, handle the weekend case
      const isWeekend = !isTradingDay(today);
      
      if (isWeekend) {
        // Calculate the next trading day
        const dayOfWeek = today.getDay();
        let daysToAdd = 0;
        
        if (dayOfWeek === 0) { // Sunday
          daysToAdd = 1; // Next trading day is Monday
        } else if (dayOfWeek === 6) { // Saturday
          daysToAdd = 2; // Next trading day is Monday
        }
        
        const nextTradingDay = new Date(today);
        nextTradingDay.setDate(today.getDate() + daysToAdd);
        
        // Format the next trading day for comparison and logging
        const nextTradingDayStr = `${nextTradingDay.getFullYear()}-${String(nextTradingDay.getMonth() + 1).padStart(2, '0')}-${String(nextTradingDay.getDate()).padStart(2, '0')}`;
        console.log(`Weekend check for ${contract.contractSymbol}: Next trading day=${nextTradingDayStr}, Expiration=${expirationStr}`);
        
        // Check if the expiration date is the next trading day
        if (expirationStr === nextTradingDayStr) {
          console.log(`Contract ${contract.contractSymbol} expires on next trading day (${nextTradingDayStr}) - sending to Expiring Today tab`);
          return true;
        }
      }
      
      // If we get here, the contract is not expiring today or on the next trading day
      console.log(`Contract ${contract.contractSymbol} is NOT expiring today - sending to Active tab`);
      return false;
    } catch (error) {
      console.error(`Error comparing dates for ${contract.contractSymbol}:`, error);
      return false;
    }
  };

  // Helper function to check if a contract is expired based on expiration date
  const isExpiredContract = (contract) => {
    if (!contract || !contract.expiration) return false;

    // If already marked as expired via API, return true
    if (contract.isApiExpired === true) {
      return true;
    }

    // HARD-CODED CHECK: If today is May 3, 2025 and contract expiration is May 3, 2025,
    // it should NEVER be considered expired
    if (contract.expiration === '2025-05-03') {
      console.log(`FIXED CHECK: Contract ${contract.contractSymbol} expires TODAY (May 3) - NOT expired`);
      return false;
    }

    // IMPORTANT: Contracts expiring today should NEVER be marked as expired
    // Instead they go to the 'Expiring Today' tab
    if (isExpiringToday(contract) || contract.isExpiringToday === true) {
      console.log(`Contract ${contract.contractSymbol} expires today - sending to Expiring Today tab`);
      return false;
    }

    // Check if expiration date is before today using string comparison (most reliable)
    if (contract.expiration < getTodayDate()) {
      console.log(`Contract ${contract.contractSymbol} expired (string comparison) - ${contract.expiration} < ${getTodayDate()}`);
      return true;
    }

    // Fallback: Use Date object comparison
    try {
      // Parse the expiration date
      const expirationDate = new Date(contract.expiration);
      const todayDate = getTodayDateObject();

      // Set both dates to midnight to compare just the dates
      expirationDate.setHours(0, 0, 0, 0);

      // If the expiration date is before the current date, the contract is expired
      const isExpired = expirationDate.getTime() < todayDate.getTime();

      if (isExpired) {
        console.log(`Contract ${contract.contractSymbol} expired (date comparison) - ${expirationDate} < ${todayDate}`);
      }

      return isExpired;
    } catch (error) {
      console.error(`Error in isExpiredContract for ${contract.contractSymbol}:`, error);
      return false;
    }
  };

  // Function to update watchlist prices with date-based expiration checks
  const updateWatchlistPrices = async () => {
    if (watchlist.length === 0) {
      toast.info('No contracts in watchlist');
      return;
    }

    try {
      setIsUpdatingWatchlist(true);
      toast.info('Updating all contracts...');

      // Get current date in EST timezone
      const now = new Date();
      const estOptions = { timeZone: 'America/New_York', hour: 'numeric', hour12: false };
      const currentHourET = parseInt(new Intl.DateTimeFormat('en-US', estOptions).format(now));
      const isMarketClosed = currentHourET >= 16; // 4 PM ET or later

      console.log(`Current hour in ET: ${currentHourET}, Market closed: ${isMarketClosed}`);

      const updatedWatchlist = await Promise.all(watchlist.map(async (contract) => {
        // Create a working copy of the contract that we'll update
        let updatedContract = { ...contract };
        
        // STEP 1: Check expiration date and update isExpiringToday field
        // This determines if the contract should be in the "Expiring Today" tab
        const isContractExpiringToday = isExpiringToday(contract);
        updatedContract.isExpiringToday = isContractExpiringToday;
        
        console.log(`STEP 1: Contract ${contract.contractSymbol} expiring today? ${isContractExpiringToday}`);
        
        // STEP 2: Check price for each contract and update isApiExpired if no price is found
        // Default for new contracts should be isApiExpired = false
        if (updatedContract.isApiExpired !== true) { // Only check if not already expired
          try {
            // Use originalSymbol for API calls instead of contractSymbol
            const symbolToUse = contract.originalSymbol || contract.contractSymbol;
            console.log(`STEP 2: Updating contract using symbol: ${symbolToUse} (display name: ${contract.contractSymbol})`);

            const priceData = await optionsApi.getCurrentPrice(symbolToUse);

            // Extract all the data from the API response
            const {
              currentPrice,
              bid,
              ask,
              change,
              percentChange,
              volume,
              expired: isApiExpired,
              isExpiringToday: apiExpiringToday,
              underlyingPrice
            } = priceData;

            // If we got a price, update price-related fields
            const priceDifference = currentPrice - contract.selectedPrice;
            const percentChangeFromEntry = (priceDifference / contract.selectedPrice) * 100;

            updatedContract = {
              ...updatedContract,
              lastPrice: currentPrice,
              bid: bid,
              ask: ask,
              change: change,
              percentChange: percentChange,
              volume: volume,
              pnl: priceDifference,
              pnlPercent: percentChangeFromEntry,
              lastUpdated: new Date().toISOString(),
              // Use backend's determination of expired status
              isApiExpired: isApiExpired || false,
              isExpiringToday: apiExpiringToday || false,
              underlyingPrice: underlyingPrice
            };

            console.log(`STEP 2: Successfully updated price for ${contract.contractSymbol}: ${currentPrice}, expired: ${isApiExpired}, expiringToday: ${apiExpiringToday}`);
          } catch (error) {
            console.error(`STEP 2: Error updating ${contract.contractSymbol} (original symbol: ${contract.originalSymbol}):`, error);

            // If price not found (404/400 error), mark as API expired
            if (error.response && (error.response.status === 404 || error.response.status === 400)) {
              updatedContract.isApiExpired = true;
              console.log(`STEP 2: No price found for ${contract.contractSymbol} - marking as API expired`);
            } else {
              // For other errors, don't change the API expired status
              updatedContract.hasUpdateError = true;
              updatedContract.updateErrorMessage = error.message || 'API error occurred';
            }

            updatedContract.lastUpdated = new Date().toISOString();
          }
        } else {
          console.log(`STEP 2: Skipping price check for ${contract.contractSymbol} - already marked as expired`);
        }
        
        // STEP 3: Determine the correct tab for the contract based on flags
        console.log(`STEP 3: Determining tab for ${contract.contractSymbol} - isExpiringToday: ${updatedContract.isExpiringToday}, isApiExpired: ${updatedContract.isApiExpired}`);
        
        // Logic as specified:
        // - If isExpiringToday=false and isApiExpired=false -> Active tab
        // - If isExpiringToday=true and isApiExpired=true -> Expired tab
        // - If isExpiringToday=false and isApiExpired=true -> Expired tab
        // - If isExpiringToday=true and isApiExpired=false -> Expiring Today tab
        
        if (updatedContract.isApiExpired) {
          // Any contract with isApiExpired=true goes to Expired tab
          console.log(`STEP 3: Contract ${contract.contractSymbol} belongs in Expired tab (API expired)`);
          // No need to change anything, isApiExpired=true already puts it in Expired tab
        } else if (updatedContract.isExpiringToday) {
          // Contract with isExpiringToday=true and isApiExpired=false goes to Expiring Today tab
          console.log(`STEP 3: Contract ${contract.contractSymbol} belongs in Expiring Today tab`);
          // No need to change anything, isExpiringToday=true already puts it in Expiring Today tab
        } else {
          // Contract with isExpiringToday=false and isApiExpired=false goes to Active tab
          console.log(`STEP 3: Contract ${contract.contractSymbol} belongs in Active tab`);
          // No need to change anything, both flags false already puts it in Active tab
        }
        
        return updatedContract;
      }));

      setWatchlist(updatedWatchlist);
      saveWatchlist(updatedWatchlist);

      toast.success('All contracts updated');
    } catch (error) {
      console.error('Error updating watchlist:', error);
      toast.error('Failed to update watchlist');
    } finally {
      setIsUpdatingWatchlist(false);
    }
  };

  const handleUpdateItem = async (contractSymbol) => {
    // Find the contract in the watchlist using the contractSymbol
    const index = watchlist.findIndex(item => item.contractSymbol === contractSymbol);
    
    if (index === -1) {
      toast.error(`Contract ${contractSymbol} not found in watchlist`);
      return;
    }
    
    const contract = watchlist[index];
    
    console.log(`handleUpdateItem: Updating ${contractSymbol}, isApiExpired: ${contract.isApiExpired}, originalSymbol: ${contract.originalSymbol}`);
    
    // Create a copy of the contract and watchlist
    const updatedContract = { ...contract };
    const updatedWatchlist = [...watchlist];
    
    // Set loading state for this contract (even if expired - we want to update the price)
    updatedContract.isLoading = true;
    updatedWatchlist[index] = updatedContract;
    setWatchlist(updatedWatchlist);

    try {
      // Use the same improved API call that handles expired options
      const symbolToUse = contract.originalSymbol || contract.contractSymbol;
      console.log(`handleUpdateItem: Making API call for ${symbolToUse}`);
      const priceData = await optionsApi.getCurrentPrice(symbolToUse);
      console.log(`handleUpdateItem: Received price data:`, priceData);

      if (!priceData) {
        throw new Error(`Failed to fetch data for ${contractSymbol}`);
      }

      // Extract all the data from the API response
      const {
        currentPrice,
        bid,
        ask,
        change,
        percentChange,
        volume,
        expired: isApiExpired,
        isExpiringToday: apiExpiringToday,
        underlyingPrice
      } = priceData;

      const selectedPrice = parseFloat(contract.selectedPrice || contract.currentPrice);
      const pnlUSD = (currentPrice - selectedPrice) * 100;
      const pnlPCT = selectedPrice !== 0 ? ((currentPrice - selectedPrice) / selectedPrice) * 100 : 0;

      console.log(`handleUpdateItem: Received from API for ${contractSymbol}:`, {
        currentPrice,
        underlyingPrice,
        isApiExpired,
        bid,
        ask
      });
      
      console.log(`handleUpdateItem: Calculated for ${contractSymbol}:`, {
        currentPrice,
        selectedPrice,
        pnlUSD,
        pnlPCT
      });

      // Create the updated contract
      const updatedContract = {
        ...contract,
        currentPrice,
        bid,
        ask,
        change: change || 0,
        percentChange: percentChange || 0,
        volume: volume || 0,
        PNL_USD: pnlUSD,
        PNL_PCT: pnlPCT,
        lastUpdated: new Date().toISOString(),
        isLoading: false,
        // Use backend's determination of expired status
        isApiExpired: isApiExpired || false,
        isExpiringToday: apiExpiringToday || false,
        underlyingPrice: underlyingPrice
      };

      // Update the watchlist
      updatedWatchlist[index] = updatedContract;
      saveWatchlist(updatedWatchlist);

      toast.success(`Updated ${contractSymbol}`);
    } catch (error) {
      console.error(`Error updating ${contractSymbol}:`, error);
      
      // Check if the error is related to an expired contract that can't be found
      if (error.response?.data?.error && (
          error.response.data.error.includes('cannot be found') ||
          error.response.data.error.includes('Expiration')
      )) {
        console.log(`Contract ${contractSymbol} appears to be expired - preserving ALL fields`);
        
        // IMPORTANT: Create a copy of the original contract with minimal changes
        // Preserving ALL original field values
        const preservedContract = {...contract};
        preservedContract.isApiExpired = true;
        preservedContract.isLoading = false;
        // DO NOT modify lastUpdated - keep it exactly as it was before
        
        // Update the watchlist with the preserved contract
        updatedWatchlist[index] = preservedContract;
        saveWatchlist(updatedWatchlist);
        
        toast.info(`${contractSymbol} has expired - all data preserved`);
      } else {
        toast.error(`Failed to update ${contractSymbol}`);
        
        // Reset loading state on error
        const resetWatchlist = watchlist.map(item =>
          item.contractSymbol === contractSymbol ? { ...item, isLoading: false } : item
        );
        setWatchlist(resetWatchlist);
      }
    }
  };

  return (
    <Container>
      <TitleContainer>
        <Title>Options Chain</Title>
        <InfoIconContainer>
          <InfoIcon theme={theme} />
          <Tooltip theme={theme}>
            This tool allows you to view options contracts for a selected stock ticker and expiration date. 
            You can add contracts to your watchlist to track their prices over time.
            <br /><br />
            <strong>   
                         Data may be delayed for up to 20 minutes max.</strong>
          </Tooltip>
        </InfoIconContainer>
      </TitleContainer>
      
      <Suspense fallback={<ComponentLoader>Loading search...</ComponentLoader>}>
        <TickerSearch
          onTickerChange={handleTickerChange}
          onReset={resetTicker}
          isLoading={isLoading}
          initialTicker={ticker}
          error={tickerError}
        />
      </Suspense>

      {ticker && (
        <Suspense fallback={<ComponentLoader>Loading parameters...</ComponentLoader>}>
          <OptionsParameters
            expirations={expirations}
            selectedExpiration={selectedExpiration}
            optionType={optionType}
            onExpirationChange={handleExpirationChange}
            onOptionTypeChange={handleOptionTypeChange}
            isLoading={isLoading}
          />
        </Suspense>
      )}

      {ticker && selectedExpiration && (
        <Suspense fallback={<ComponentLoader>Loading Options Chain...</ComponentLoader>}>
          <OptionsChain
            options={optionsChain}
            onAddToWatchlist={addToWatchlist}
            isLoading={isLoading}
          />
        </Suspense>
      )}

      <Suspense fallback={<ComponentLoader>Loading watchlist...</ComponentLoader>}>
        <Watchlist
          watchlist={watchlist}
          onUpdateItem={handleUpdateItem}
          onRemoveItem={removeFromWatchlist}
          onClearWatchlist={handleClearWatchlist}
          onUpdateAll={updateWatchlistPrices}
          isUpdatingAll={isUpdatingWatchlist}
          isLoading={isLoading}
        />
      </Suspense>

      {showDuplicateWarning && pendingContract && (
        <DialogOverlay>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Duplicate Contract</DialogTitle>
              <CloseButton theme={theme} onClick={() => handleDuplicateWarningResponse(false)}>
                <FaTimes />
              </CloseButton>
            </DialogHeader>

            <p>This contract is already in your watchlist:</p>
            <p><strong>{pendingContract.contractSymbol}</strong></p>
            <p>Are you sure you want to add it again?</p>

            <CheckboxContainer onClick={() => setDontShowAgain(!dontShowAgain)} theme={theme}>
              <CheckboxInput theme={theme}
                type="checkbox"
                checked={dontShowAgain}
                onChange={() => setDontShowAgain(!dontShowAgain)}
              />
              Don't show this warning again
            </CheckboxContainer>

            <DialogActions>
              <CancelButton onClick={() => handleDuplicateWarningResponse(false)}>
                Cancel
              </CancelButton>
              <ConfirmButton onClick={() => handleDuplicateWarningResponse(true)}>
                Add Anyway
              </ConfirmButton>
            </DialogActions>
          </DialogContent>
        </DialogOverlay>
      )}

      {showClearConfirmation && (
        <DialogOverlay>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Clear Watchlist</DialogTitle>
              <CloseButton theme={theme} onClick={() => setShowClearConfirmation(false)}>
                <FaTimes />
              </CloseButton>
            </DialogHeader>

            <p style={{ marginBottom: '1.5rem', color: theme.mode === 'dark' ? '#d0d0d0' : '#555' }}>
              Are you sure you want to remove all {watchlist.length} contract{watchlist.length !== 1 ? 's' : ''} from your watchlist? This action cannot be undone.
            </p>

            <DialogActions>
              <CancelButton onClick={() => setShowClearConfirmation(false)}>
                Cancel
              </CancelButton>
              <ConfirmButton onClick={() => {
                clearWatchlist([]);
                // Ensure dialog is closed
                setShowClearConfirmation(false);
              }}>
                Clear All
              </ConfirmButton>
            </DialogActions>
          </DialogContent>
        </DialogOverlay>
      )}
    </Container>
  );
};

export default OptionsWatchlist;
